import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n, labelOf } from "@/i18n/I18nProvider";

/**
 * DynamicField — renders a single configurable form field.
 * Field shape: { key, type: text|tel|number|textarea, label:{en,..}, placeholder:{...}, helper:{...}, required, active }
 */
export default function DynamicField({ field, value, onChange, error, testIdPrefix = "field" }) {
  const { lang, t } = useI18n();
  if (!field || field.active === false) return null;

  const id = `${testIdPrefix}-${field.key}`;
  const label = labelOf(field.label, lang, field.key);
  const placeholder = labelOf(field.placeholder, lang, "");
  const helper = labelOf(field.helper, lang, "");
  const inputClass = "mt-2 h-12 rounded-2xl border-brand-line bg-white px-4 text-base focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:border-brand-primary";

  return (
    <div>
      <Label htmlFor={id} className="font-sans text-xs uppercase tracking-[0.18em] text-brand-dark font-bold">
        {label}{!field.required && <span className="text-brand-muted normal-case tracking-normal font-medium ml-1">· {t("common.optional")}</span>}
      </Label>
      {field.type === "textarea" ? (
        <Textarea
          id={id}
          data-testid={`${testIdPrefix}-${field.key}-input`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-2 min-h-[88px] rounded-2xl border-brand-line bg-white px-4 py-3 text-base focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:border-brand-primary"
        />
      ) : (
        <Input
          id={id}
          data-testid={`${testIdPrefix}-${field.key}-input`}
          type={field.type === "number" ? "number" : field.type === "tel" ? "tel" : "text"}
          inputMode={field.type === "tel" || field.type === "number" ? "numeric" : undefined}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={
            field.key === "name" ? "name" :
            field.key === "phone" || field.key === "alt_phone" ? "tel" :
            field.key === "address" ? "street-address" :
            field.key === "city" ? "address-level2" :
            field.key === "state" ? "address-level1" :
            field.key === "pincode" ? "postal-code" : undefined
          }
          className={inputClass}
        />
      )}
      {helper && !error && <p className="text-xs text-brand-muted mt-1.5 leading-snug">{helper}</p>}
      {error && <p className="text-xs text-brand-primary mt-1.5 font-medium">{error}</p>}
    </div>
  );
}

/**
 * Validate dynamically — returns {errors, ok}.
 * Hard-coded validation rules for known keys, plus required check.
 */
export function validateFields(fields, value, t) {
  const errors = {};
  for (const f of fields) {
    if (f.active === false) continue;
    const v = String(value[f.key] || "").trim();
    if (f.required && !v) {
      errors[f.key] = t("common.required") || "Required";
      continue;
    }
    if (!v) continue;
    if (f.key === "phone" || f.key === "alt_phone") {
      if (!/^[0-9+\-\s]{8,15}$/.test(v)) errors[f.key] = "Invalid phone";
    }
    if (f.key === "pincode") {
      if (!/^[0-9]{4,8}$/.test(v)) errors[f.key] = "Invalid pincode";
    }
    if (f.key === "age") {
      const n = Number(v);
      if (Number.isNaN(n) || n < 10 || n > 99) errors[f.key] = "Invalid age";
    }
  }
  return { errors, ok: Object.keys(errors).length === 0 };
}
